import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import BtnTaratic from '../components/ui/BtnTaratic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelopeOpenText,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('El enlace es inválido o ha expirado.');
  const navigate = useNavigate();

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        return;
      }
      try {
        await authService.verifyEmail(token);
        setStatus('success');
      } catch (err: any) {
        console.error('Email verification error:', err);
        const detail = err?.response?.data?.detail;
        setErrorMessage(
          (typeof detail === 'string' ? detail : detail?.message) ||
          err?.response?.data?.message ||
          'No se pudo verificar el correo. Inténtalo de nuevo.'
        );
        setStatus('error');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center dark-bg p-4 sm:p-6 lg:p-8 overflow-x-hidden">
      <div className="relative bg-black/50 backdrop-filter backdrop-blur-xl p-8 sm:p-10 rounded-3xl shadow-2xl w-full max-w-md text-center shadow-inner-glow shadow-inner-glow-black-md">
        <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mx-auto mb-5 text-indigo-400 text-2xl shadow-inner-glow">
          <FontAwesomeIcon icon={faEnvelopeOpenText} className="icon-inner-glow-white" />
        </div>

        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-6 tracking-tight">
          Verificación de Correo
        </h2>
        
        {status === 'loading' && (
          <div className="flex flex-col items-center justify-center py-6">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-indigo-400 mb-4" />
            <p className="text-white/70 text-sm sm:text-base font-medium">Verificando tu enlace...</p>
          </div>
        )}
        
        {status === 'success' && (
          <div>
            <div className="bg-green-500/10 border border-green-500/20 p-5 rounded-2xl mb-8 shadow-inner-glow">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 text-green-400 text-xl">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <p className="text-green-300 font-medium text-sm sm:text-base">
                Tu correo electrónico ha sido verificado con éxito.
              </p>
            </div>
            <BtnTaratic
              color="green"
              size="md"
              className="w-full flex justify-center items-center gap-2 py-3 text-base font-semibold"
              onClick={() => navigate('/login')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
              <span>Ir a Iniciar Sesión</span>
            </BtnTaratic>
          </div>
        )}
        
        {status === 'error' && (
          <div>
            <div className="bg-red-500/10 border border-red-500/20 p-5 rounded-2xl mb-8 shadow-inner-glow">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3 text-red-400 text-xl">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <p className="text-red-300 font-medium text-sm sm:text-base">
                {errorMessage}
              </p>
            </div>
            <BtnTaratic
              color="ghost-dark"
              size="md"
              className="w-full flex justify-center items-center gap-2 py-3 text-base font-semibold"
              onClick={() => navigate('/login')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
              <span>Volver al Login</span>
            </BtnTaratic>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
